import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {useQueryClient} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useEffect, useState} from 'react';
import ReactJson from 'react-json-view';
import {useDispatch, useSelector} from 'react-redux';

import CustomAlert from '../../components/CustomAlert/CustomAlert';
import CustomButton from '../../components/CustomButton/CustomButton';
import CustomDrawer from '../../components/CustomDrawer/CustomDrawer';
import CustomStepper from '../../components/CustomStepper/CustomStepper';
import {ConfigConstant} from '../../constants/config';
import ErrorConstant from '../../constants/errors';
import {checkMenuProcessing} from '../../service/OpsRaw';
import {triggerUpdateTableData} from '../../store/actions/triggerUpdateTableData';
import {pxToRem} from '../../utils';

const StyledHeaderText = styled(Typography)(({theme, label}) => ({
  fontSize: pxToRem(20),
  fontWeight: 800,
  '& span': {
    pading: pxToRem(4),
    color:
      label === ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS
        ? theme.palette.success.dark
        : label === ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED
          ? theme.palette.error.dark
          : theme.palette.warning.dark,
  },
}));

const StyledTypography = styled(Typography)(({theme, isaddon}) => ({
  fontSize: isaddon === 'true' ? pxToRem(11) : pxToRem(12),
  flex: 1,
}));

const StyledHeaderBox = styled(Box)(({theme, boxno}) => ({
  margin: `${pxToRem(8)} 0 ${pxToRem(8)} 0`,
  padding: pxToRem(14),
  backgroundColor: theme.palette.primary[`light${boxno}`] || theme.palette.primary.light,
  borderRadius: pxToRem(4),
}));

const StyledFlexBox = styled(Box)(({theme}) => ({
  marginLeft: pxToRem(8),
  marginRight: pxToRem(8),
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledCustomButton = styled(CustomButton)(({theme}) => ({
  width: pxToRem(128),
}));

const StyledJSONBox = styled(Box)(({theme}) => ({
  margin: `${pxToRem(8)} ${pxToRem(24)} ${pxToRem(8)} ${pxToRem(24)}`,
  padding: pxToRem(16),
  backgroundColor: theme.palette.grey[200],
  borderRadius: pxToRem(8),
}));

const getOperationSteps = (selectedRow) => {
  const steps = ['Queued At Hub', 'Processed By Hub', 'Sent'];
  if (
    ConfigConstant.MONGO_CONFIG?.OPERATION_TYPES?.MENU_PUSH?.code === selectedRow.row?.operationType &&
    ConfigConstant.MONGO_CONFIG?.CHANNEL_SUPPORT_ASYNC_MENU_PROCESSING.includes(selectedRow.row?.channelGroupCode)
  ) {
    steps.push('Accepted By Aggregator', 'Queued At Aggregator');
  }
  steps.push('Processed By Aggregator');
  return steps;
};

const getCurrentStepIndex = (selectedRow) => {
  let errorIndex = -1;
  let completedIndex = 1;
  switch (selectedRow.row?.subStatus) {
    case ConfigConstant.MONGO_CONFIG?.SUB_STATUSES?.PROCESSED_BY_HUB_FAILED.code:
      errorIndex = 1;
      completedIndex = 0;
      break;
    case ConfigConstant.MONGO_CONFIG?.SUB_STATUSES?.PROCESSED_BY_AGGREGATOR_FAILED.code:
      errorIndex =
        ConfigConstant.MONGO_CONFIG?.OPERATION_TYPES?.MENU_PUSH?.code === selectedRow.row?.operationType
          ? ConfigConstant.MONGO_CONFIG?.CHANNEL_SUPPORT_ASYNC_MENU_PROCESSING.includes(
              selectedRow.row?.channelGroupCode
            )
            ? 5
            : 3
          : 3;
      completedIndex = 4;
      break;
    case ConfigConstant.MONGO_CONFIG?.SUB_STATUSES?.ACCEPTED_BY_AGGREGATOR_FAILED.code:
      errorIndex = 3;
      completedIndex = 2;
      break;
    case ConfigConstant.MONGO_CONFIG?.SUB_STATUSES?.QUEUED_AT_AGGREGATOR.code:
      completedIndex = 4;
      break;
    case ConfigConstant.MONGO_CONFIG?.SUB_STATUSES?.PROCESSED_BY_AGGREGATOR_SUCCESS.code:
      completedIndex = 5;
      break;
    default:
      errorIndex = -1;
      completedIndex = 1;
  }
  return {errorIndex, completedIndex};
};

const getShowCheckStatusButton = (selectedRow, isMenuPushOps) => {
  return (
    selectedRow.row?.status === ConfigConstant.MONGO_CONFIG.STATUSES.QUEUED &&
    isMenuPushOps &&
    ConfigConstant.MONGO_CONFIG.CHANNEL_SHOW_CHECK_MENU_STATUSES.includes(selectedRow.row?.channelGroupCode)
  );
};

const getCheckStatusOption = (selectedRow) => {
  const {channelGroupCode, rebelCorrelationId, channelStoreCode} = selectedRow.row;
  let channelCorrelationId = null;
  switch (channelGroupCode) {
    case ConfigConstant.CODE_SWIGGY:
    case ConfigConstant.CODE_CAREEM:
      channelCorrelationId = selectedRow.row?.response?.data?.request_id;
      break;
    default:
      channelCorrelationId = null;
  }
  return {channelGroupCode, rebelCorrelationId, channelCorrelationId, channelStoreCode};
};

const getshowProcessedResponse = (selectedRow, isMenuPushOps) => {
  return (
    isMenuPushOps &&
    ConfigConstant.MONGO_CONFIG?.CHANNEL_SUPPORT_ASYNC_MENU_PROCESSING.includes(selectedRow.row?.channelGroupCode) &&
    selectedRow.row?.status !== ConfigConstant.MONGO_CONFIG?.STATUSES?.QUEUED &&
    selectedRow.row?.processedResponse
  );
};

const OperationRawDrawer = ({open, onToggleOff, selectedRow, filters, dataQuery}) => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const operationFilter = filters?.find((filter) => filter.id === 'Operation');

  const isMenuPushOps = operationFilter?.value?.includes(ConfigConstant.MONGO_CONFIG.OPERATION_TYPES.MENU_PUSH.code);

  const [loading, setLoading] = useState(false);

  const [alertData, setAlertData] = useState({isOpen: false, text: '', severity: 'info'});

  const {errorIndex, completedIndex} = getCurrentStepIndex(selectedRow);

  const operationSteps = getOperationSteps(selectedRow);

  const showCheckStatusButton = getShowCheckStatusButton(selectedRow, isMenuPushOps);

  const showProcessedResponse = getshowProcessedResponse(selectedRow, isMenuPushOps);

  const permissions = useSelector((state) => state.permissions);
  const {admin: isAdmin} = permissions;

  const updateTableRow = ({status, subSatus, processedResponse}) => {
    const newData = {
      totalCount: dataQuery.data?.totalCount,
      rows: [...(dataQuery.data?.rows || [])],
    };
    newData.rows[selectedRow.index].status = status;
    newData.rows[selectedRow.index].subStatus = subSatus;
    newData.rows[selectedRow.index].processedResponse = processedResponse;
    queryClient.setQueryData(['data-opsRaw'], newData);
    triggerUpdateTableData(dispatch, Date.now());
  };

  const handleCheckStatusClick = async () => {
    try {
      setLoading(true);
      const {channelGroupCode, rebelCorrelationId, channelCorrelationId, channelStoreCode} =
        getCheckStatusOption(selectedRow);
      if (!channelCorrelationId) {
        return setAlertData((prevState) => ({
          ...prevState,
          isOpen: true,
          text: `Failed to get channelCorrelationId.`,
          severity: 'error',
        }));
      }
      const response = await checkMenuProcessing({
        channelStoreCode,
        channelGroupCode,
        channelCorrelationId,
        rebelCorrelationId,
        filters,
      });
      if (response?.data?.data?.data?.status === ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS) {
        setAlertData((prevState) => ({
          ...prevState,
          isOpen: true,
          text: `Menu is successfully processed by ${selectedRow.row?.channelGroupName}.`,
          severity: 'success',
        }));
        updateTableRow({
          status: ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS,
          subSatus: ConfigConstant.MONGO_CONFIG?.SUB_STATUSES?.PROCESSED_BY_AGGREGATOR_SUCCESS?.code,
          processedResponse: response?.data?.data?.data?.processedResponse,
        });
      } else if (response?.data?.data?.data?.status === ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED) {
        setAlertData((prevState) => ({
          ...prevState,
          isOpen: true,
          text: `Menu processing failed. Please check below processed response section for reason.`,
          severity: 'error',
        }));
        updateTableRow({
          status: ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED,
          subSatus: ConfigConstant.MONGO_CONFIG?.SUB_STATUSES?.PROCESSED_BY_AGGREGATOR_FAILED?.code,
          processedResponse: response?.data?.data?.data?.processedResponse,
        });
      } else {
        setAlertData((prevState) => ({
          ...prevState,
          isOpen: true,
          text: `Not yet processed. Please check back after sometime.`,
          severity: 'warning',
        }));
      }
      setLoading(false);
    } catch (error) {
      setAlertData((prevState) => ({
        ...prevState,
        isOpen: true,
        text: `Error: ${error?.message || ErrorConstant.generic}`,
        severity: 'error',
      }));
      setLoading(false);
    }
  };

  const closeAlert = () => {
    setAlertData((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };

  useEffect(() => {
    const timeId = setTimeout(() => {
      closeAlert();
    }, 5000);
    return () => {
      clearTimeout(timeId);
    };
  }, [alertData.isOpen]);

  return (
    <CustomDrawer
      open={open}
      onToggleOff={onToggleOff}
      anchor={'right'}
      title={'Operation Details'}
      actionButtons={false}
    >
      {loading && <LinearProgress />}
      <CustomAlert
        isOpen={alertData.isOpen}
        closeAlert={closeAlert}
        text={alertData.text}
        severity={alertData.severity}
      />
      <Box sx={{padding: pxToRem(16), display: 'flex', justifyContent: 'space-between'}}>
        <Box>
          <StyledHeaderText label={selectedRow.row?.status}>
            {selectedRow.row.channelName !== selectedRow.row.channelGroupName
              ? `${selectedRow.row.channelGroupName}-${selectedRow.row.channelName} `
              : `${selectedRow.row.channelGroupName}-direct `}
            {ConfigConstant.MONGO_CONFIG.OPERATION_TYPES[selectedRow.row?.operationType]?.label}
            {' : '}
            <span>{selectedRow.row?.status}</span>
          </StyledHeaderText>
          <Typography sx={{color: (theme) => theme.palette.grey[600], fontSize: pxToRem(14)}}>
            {selectedRow.row?.rebelCorrelationId}
          </Typography>
          <Box sx={{display: 'flex', gap: pxToRem(12), alignItems: 'center', flexWrap: 'wrap'}}>
            <Typography sx={{color: (theme) => theme.palette.grey[600], fontSize: pxToRem(14)}}>
              {format(new Date(selectedRow.row.timestamp), 'dd MMM yyyy HH:mm:ss')}
            </Typography>
            {selectedRow.row?.callbackTimestamp &&
              (() => {
                const diffMs =
                  new Date(selectedRow.row.callbackTimestamp).getTime() - new Date(selectedRow.row.timestamp).getTime();
                const diffSec = Math.floor(diffMs / 1000);
                const diffMin = Math.floor(diffSec / 60);
                const diffHr = Math.floor(diffMin / 60);
                const tookLabel =
                  diffHr > 0
                    ? `${diffHr}h ${diffMin % 60}m ${diffSec % 60}s`
                    : diffMin > 0
                      ? `${diffMin}m ${diffSec % 60}s`
                      : `${diffSec}s`;
                return (
                  <Typography sx={{color: (theme) => theme.palette.grey[600], fontSize: pxToRem(14)}}>
                    {`→ Callback At: ${format(new Date(selectedRow.row.callbackTimestamp), 'HH:mm:ss')} (${tookLabel})`}
                  </Typography>
                );
              })()}
          </Box>
        </Box>
        {showCheckStatusButton && (
          <Box>
            <StyledCustomButton
              text={'Check Status'}
              variant="outlined"
              onClick={handleCheckStatusClick}
              isLoading={loading}
            />
          </Box>
        )}
      </Box>
      <CustomStepper steps={operationSteps} completedIndex={completedIndex} errorIndex={errorIndex} />

      <Box sx={{mt: 2}}>
        <StyledHeaderBox>
          <Typography sx={{fontSize: pxToRem(18)}}>Basic Details</Typography>
        </StyledHeaderBox>
        <StyledFlexBox>
          <StyledTypography>Virtual Store Code:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>{selectedRow.row?.partnerStoreCode || '-'}</StyledTypography>
          <StyledTypography>Brand:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>{`${selectedRow.row?.partnerBrandCode || '-'} ${
            selectedRow.row?.partnerBrandName || ''
          }`}</StyledTypography>
        </StyledFlexBox>
        <StyledFlexBox>
          <StyledTypography>Channel Store Code:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>{selectedRow.row?.channelStoreCode || '-'}</StyledTypography>
          <StyledTypography>Store:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>
            {`${selectedRow.row?.partnerPhyStoreCode || ''} ${selectedRow.row?.partnerPhyStoreName || ''} ${
              selectedRow.row?.city || ''
            } ${selectedRow.row?.countryName || ''} `}
          </StyledTypography>
        </StyledFlexBox>
        <StyledFlexBox>
          <StyledTypography>Is Bulk Toggle:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>
            {selectedRow.row?.isBulkToggle != null ? (selectedRow.row.isBulkToggle ? 'Yes' : 'No') : '-'}
          </StyledTypography>
          <StyledTypography>Bulk Toggle Batch Id:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>{selectedRow.row?.bulkToggleBatchId || '-'}</StyledTypography>
        </StyledFlexBox>
        <StyledFlexBox>
          <StyledTypography>Source:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>{selectedRow.row?.toggleSource || '-'}</StyledTypography>
          <StyledTypography>Reason:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>{selectedRow.row?.toggleReason || '-'}</StyledTypography>
        </StyledFlexBox>
      </Box>
      <Box sx={{mt: 2}}>
        <StyledHeaderBox boxno={2}>
          <Typography sx={{fontSize: pxToRem(18)}}>Request</Typography>
        </StyledHeaderBox>
        <StyledFlexBox>
          <StyledTypography>Timestamp:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>
            {selectedRow.row?.request?.timestamp
              ? format(new Date(selectedRow.row?.request?.timestamp), 'dd MMM yyyy HH:mm:ss')
              : '-'}
          </StyledTypography>
          <StyledTypography>Status Code:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>{selectedRow.row?.request?.statusCode || '-'}</StyledTypography>
        </StyledFlexBox>
        {isMenuPushOps ? (
          <StyledFlexBox>
            <StyledTypography>Download Menu:</StyledTypography>
            <StyledTypography sx={{flex: 2}}>
              <Link href={selectedRow.row?.request?.urlS3} target="_blank">
                link to download
              </Link>
            </StyledTypography>
            <StyledTypography></StyledTypography>
            <StyledTypography sx={{flex: 2}}></StyledTypography>
          </StyledFlexBox>
        ) : null}
        <StyledJSONBox>
          <ReactJson src={selectedRow.row?.request?.data} name={'request'} displayDataTypes={false} collapsed={true} />
        </StyledJSONBox>
      </Box>
      <Box sx={{mt: 2}}>
        <StyledHeaderBox boxno={3}>
          <Typography sx={{fontSize: pxToRem(18)}}>Response</Typography>
        </StyledHeaderBox>
        <StyledFlexBox>
          <StyledTypography>Timestamp:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>
            {selectedRow.row?.response?.timestamp
              ? format(new Date(selectedRow.row?.response?.timestamp), 'dd MMM yyyy HH:mm:ss')
              : '-'}
          </StyledTypography>
          <StyledTypography>Status Code:</StyledTypography>
          <StyledTypography sx={{flex: 2}}>{selectedRow.row?.response?.statusCode || '-'}</StyledTypography>
        </StyledFlexBox>
        <StyledJSONBox>
          <ReactJson
            src={selectedRow.row?.response?.data}
            name={'response'}
            displayDataTypes={false}
            collapsed={true}
          />
        </StyledJSONBox>
      </Box>
      {showProcessedResponse && (
        <Box sx={{mt: 2}}>
          <StyledHeaderBox boxno={3}>
            <Typography sx={{fontSize: pxToRem(18)}}>Processed Response</Typography>
          </StyledHeaderBox>
          <StyledFlexBox>
            <StyledTypography>Timestamp:</StyledTypography>
            <StyledTypography sx={{flex: 2}}>
              {selectedRow.row?.processedResponse?.timestamp
                ? format(new Date(selectedRow.row?.processedResponse?.timestamp), 'dd MMM yyyy HH:mm:ss')
                : '-'}
            </StyledTypography>
            <StyledTypography>Status Code:</StyledTypography>
            <StyledTypography sx={{flex: 2}}>{selectedRow.row?.processedResponse?.statusCode || '-'}</StyledTypography>
          </StyledFlexBox>
          <StyledJSONBox>
            <ReactJson
              src={selectedRow.row?.processedResponse?.data}
              name={'processedResponse'}
              displayDataTypes={false}
            />
          </StyledJSONBox>
        </Box>
      )}
      {isAdmin && (
        <Box sx={{mt: 2}}>
          <StyledHeaderBox boxno={4}>
            <Typography sx={{fontSize: pxToRem(18)}}>Meta Info</Typography>
          </StyledHeaderBox>
          <StyledJSONBox>
            <ReactJson src={selectedRow.row?.metaInfo?.kafka} name={'kafka'} displayDataTypes={false} />
          </StyledJSONBox>
        </Box>
      )}
    </CustomDrawer>
  );
};

export default OperationRawDrawer;
